import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type DuplicateQueueResult,
  type PreviewMergeResult,
} from '@whinerp/client';
import { Button, ErrorState, InlineAction, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * Setup > Possible duplicates (F-017).
 *
 * Nothing on this screen changes anything until a merge is confirmed, and a
 * merge cannot be confirmed without the preview being on screen first: a merge
 * is the one action in this product that destroys somebody else's data and
 * cannot be explained afterwards by looking at what is left.
 */
export function DuplicatesPanel(): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<{ left: string; right: string } | null>(null);

  const queue = useQuery<DuplicateQueueResult, ApiError>({
    queryKey: ['duplicate-queue'],
    queryFn: () => api.duplicateQueue({ query: { objectKey: 'contact' } }),
  });

  const preview = useQuery<PreviewMergeResult, ApiError>({
    queryKey: ['merge-preview', open],
    queryFn: () =>
      api.previewMerge({
        path: { objectKey: 'contact', recordId: open!.left },
        body: { loserId: open!.right },
      }),
    enabled: open !== null,
  });

  const merge = useMutation({
    mutationFn: () =>
      api.mergeRecords({
        path: { objectKey: 'contact', recordId: open!.left },
        body: { loserId: open!.right },
      }),
    onSuccess: async () => {
      setOpen(null);
      await queryClient.invalidateQueries({ queryKey: ['duplicate-queue'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (pair: { left: string; right: string }) =>
      api.dismissDuplicate({
        path: { objectKey: 'contact', recordId: pair.left },
        body: { otherId: pair.right },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['duplicate-queue'] });
    },
  });

  if (queue.isPending) return <Skeleton className="h-block-md w-full" />;
  if (queue.isError) {
    return <ErrorState title={t('state.error')} body={queue.error.message} onRetry={() => void queue.refetch()} />;
  }

  return (
    <Panel data-region="duplicates" id="duplicates">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('dup.queue')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('dup.queue.body')}</p>

        {queue.data.pairs.length === 0 ? (
          <p data-duplicates-none className="text-dense text-ink-meta">
            {t('dup.queue.none')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {queue.data.pairs.map((pair) => (
              <li
                key={`${pair.leftId}:${pair.rightId}`}
                data-duplicate-pair={`${pair.leftId}:${pair.rightId}`}
                className="flex flex-wrap items-center gap-2 border-b border-b-rule pb-2 text-dense text-ink"
              >
                <span>{pair.leftLabel}</span>
                <span className="text-ink-meta">·</span>
                <span>{pair.rightLabel}</span>
                <span data-pair-reason={pair.reason} className="rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600">
                  {t(`dup.reason.${pair.reason}` as never)}
                </span>
                <InlineAction
                  data-pair-merge={`${pair.leftId}:${pair.rightId}`}
                  onClick={() => setOpen({ left: pair.leftId, right: pair.rightId })}
                >
                  {t('dup.merge')}
                </InlineAction>
                <InlineAction
                  data-pair-dismiss={`${pair.leftId}:${pair.rightId}`}
                  onClick={() => dismiss.mutate({ left: pair.leftId, right: pair.rightId })}
                >
                  {t('dup.notDuplicate')}
                </InlineAction>
              </li>
            ))}
          </ul>
        )}

        {/*
          The preview. Shown BEFORE anything is written, field by field, and the
          confirm button does not exist until it is on screen.
        */}
        {open && preview.data ? (
          <div data-merge-preview className="flex flex-col gap-2 rounded-card border border-rule p-3">
            <p className="text-micro uppercase tracking-label text-ink-label">{t('dup.merge.preview')}</p>
            <div data-scroll-container className="overflow-x-auto">
            <table className="w-full text-dense">
              <tbody>
                {preview.data.fields
                  .filter((field) => field.differs)
                  .map((field) => (
                    <tr key={field.field} data-merge-field={field.field}>
                      <td className="p-1 text-ink-meta">{field.field}</td>
                      <td className="p-1" data-merge-result={field.chosen}>
                        {field.resultValue ?? '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>
            {preview.data.related.map((related) => (
              <p key={related.table} data-merge-related={related.table} className="text-meta text-ink-meta">
                {t('dup.merge.moving', { values: { count: String(related.moving) } })}
                {related.redundant > 0
                  ? ` · ${t('dup.merge.redundant', { values: { count: String(related.redundant) } })}`
                  : ''}
              </p>
            ))}
            <div>
              <Button intent="primary" size="small" data-merge-confirm busy={merge.isPending} onClick={() => merge.mutate()}>
                {t('dup.merge.confirm')}
              </Button>
            </div>
            {merge.isError ? (
              <p data-merge-refusal className="text-dense text-danger-text">
                {errorMessage(merge.error)}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
